import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { APP_NAME, roleLabel } from "@/config";
import { Page, PageHeader, Card, Segmented, Loading, int, usd, ago, plural } from "@/components/common";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldX, RefreshCw } from "lucide-react";

// design_handoff_crewbench/screens/Audit Log.dc.html: the company's tamper-evident log (backend/audit.py), each entry
// in words, with its action id, who did it, what it was about, and its hash.
const SCOPE = { coder_daily: "Daily budget", coder_monthly: "Monthly budget", company: "Company budget", project: "Project budget",
                objective: "Objective budget", session: "Session cap" };
const LABELS = {
  change_report_created: "Change report created", session_created: "Session created", session_submitted: "Session submitted for review",
  session_unlocked: "Session unlocked", session_unlock_failed: "Session unlock failed", session_ended: "Session ended",
  session_cap_set: "Session cap changed", turn_complete: "Turn finished", push_failed: "Push to GitHub failed",
  session_mcp_server_unavailable: "MCP server didn't start",
  budget_blocked: "Request stopped by a budget", memory_proposed: "Shared memory entry proposed", memory_approved: "Shared memory entry approved",
  memory_rejected: "Shared memory entry rejected", memory_edited: "Shared memory rules edited", memory_rollback: "Shared memory rolled back",
  objective_created: "Objective created", objective_updated: "Objective changed", project_created: "Project created",
  repo_access_checked: "Repository access checked", deploy_key_created: "Deploy key made", deploy_key_rotated: "Deploy key replaced",
  invite_created: "Invite sent", invite_accepted: "Invite accepted", member_updated: "Team member changed", company_created: "Company created",
  passkey_added: "Passkey added", passkey_removed: "Passkey removed", passkeys_removed_by_owner: "Passkeys removed by a founder",
  login: "Signed in", password_reset_requested: "Password reset requested", password_reset: "Password reset",
  api_key_added: "API key added", api_key_removed: "API key deleted", github_connected: "GitHub connected",
  github_disconnected: "GitHub disconnected", settings_updated: "Settings changed", billing_mode_changed: "AI access changed",
  destructive_action_refused: "Protected action refused: wrong password", seed_complete: "Demo data seeded",
  connector_connected: "AI app connected (read only)", connector_disconnected: "AI app disconnected",
  connector_token_reuse: "AI app's sign-in reused: disconnected",
};
const CATEGORY = [
  ["security", /^(session_unlock|session_locked|passkey|login|password|admin_password|api_key|github_|destructive_|settings_updated|account_|connector_)/],
  ["sessions", /^(session_|change_report|turn_complete|push_failed)/],
  ["objectives", /^(objective_|project_|repo_access|deploy_key)/],
  ["budgets", /^(budget_|credit|plan_|payment_|checkout_|billing_|model_blocked|upstream_)/],
  ["memory", /^memory_/],
  ["team", /^(invite_|member_|company_)/],
];
const CATS = [["all", "All"], ["sessions", "Sessions"], ["objectives", "Objectives"], ["budgets", "Budgets"], ["memory", "Shared memory"], ["team", "Team"], ["security", "Security"]];
const category = (a) => (CATEGORY.find(([, re]) => re.test(a)) || ["other"])[0];
const human = (a) => a.replace(/owner/g, "founder").replace(/coder/g, "independent_professional").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

function label(e) {
  if (e.action === "session_locked") return e.actor_role === "coder" ? "Session locked" : "Session locked automatically";
  if (e.action === "budget_alert") {
    const what = SCOPE[e.meta?.scope] || "Budget";
    return e.meta?.threshold >= 100 ? `${what} reached` : `${what} at ${e.meta?.threshold}%`;
  }
  if (e.action === "budget_set") return `${SCOPE[(e.target || "").split(":")[0]] || "Budget"} changed`;
  return LABELS[e.action] || human(e.action);
}

// what an entry was about, from its target id and metadata (never content)
function on(e, n) {
  const t = e.target || "";
  const m = e.meta || {};
  const session = () => `Session ${t.slice(0, 6)}`;
  if (e.action === "session_mcp_server_unavailable") return [session(), m.server, m.reason].filter(Boolean).join(" · ");
  if (/^(session_|turn_complete|push_failed|memory_proposed)/.test(e.action)) return session();
  if (e.action === "change_report_created") return [session(), m.files != null && `${int(m.files)} files`, m.flags?.length && `${m.flags.length} flags`].filter(Boolean).join(" · ");
  if (/^objective_/.test(e.action)) return [n.objectives[t] || "An objective", Array.isArray(m) && m.length && m.join(", ")].filter(Boolean).join(" · ");
  if (/^(project_|repo_access|deploy_key|memory_edited)/.test(e.action)) return n.projects[t] || "A project";
  if (e.action === "memory_rollback") return `${n.projects[t] || "A project"} · back to version ${m.to_version}`;
  if (e.action === "budget_set") { const ref = t.split(":")[1]; return [n.people[ref] || n.projects[ref] || n.objectives[ref] || n.company, m.amount_usd != null && usd(m.amount_usd)].filter(Boolean).join(" · "); }
  if (e.action === "budget_alert") { const ref = t.split(":")[1]; return n.people[ref] || n.projects[ref] || n.objectives[ref] || (t.startsWith("session:") ? `Session ${ref.slice(0, 6)}` : n.company); }
  if (/^(member_updated|member_removed|passkeys_removed_by_owner)/.test(e.action)) return n.people[t] || "A team member";
  if (e.action === "seats_bought" && m.to) return [plural(m.to.employee || 0, "extra seat"), plural(m.to.independent || 0, "extra slot")].join(", ") + " bought ahead";
  // hiring terms (assignment_routes.py): whose task and which objective, then the fee
  if (/^(task_|fee_change_|terms_agreed)/.test(e.action)) {
    const a = n.assignments[t] || {};
    const fee = m.to ?? m.fee_usd;
    return [n.people[a.person_id], n.objectives[a.objective_id], fee != null && usd(fee)].filter(Boolean).join(" · ") || "A task";
  }
  if (/@/.test(t) || (t && t.length < 40 && !/^[0-9a-f-]{20,}$/.test(t))) return t;
  return "—";
}

function who(e, n) {
  if (!e.actor_role || e.actor_role === "system") return [APP_NAME, "Platform"];
  if (e.actor_role === "platform_admin") return [`${APP_NAME} staff`, "Platform admin"];
  if (e.actor_role === "payment") return [APP_NAME, "Payment"];  // credits a payment brought (backend/credits.py), nobody's own action
  // the person's title now (Team member or Independent professional), else the role the entry recorded
  return [n.people[e.actor_id] || "A former member", n.titles[e.actor_id] || roleLabel(e.actor_role)];
}

export default function Audit() {
  const [cat, setCat] = useState("all");
  const { data, isLoading, refetch, isFetching, dataUpdatedAt } = useQuery({ queryKey: ["audit"], queryFn: async () => (await api.get("/audit")).data });
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data });
  const { data: objs } = useQuery({ queryKey: ["objectives"], queryFn: async () => (await api.get("/objectives")).data });
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const header = (status) => <PageHeader eyebrow="Audit log · Append-only" title="Every change of state in your company, in order." status={status} />;
  if (isLoading || !data) return <Page className="gap-5">{header()}<Loading /></Page>;

  const v = data.verification;
  const n = {
    people: { ...Object.fromEntries((team?.members || []).map((m) => [m.id, m.name || m.email])),
      ...Object.fromEntries(Object.entries(data?.people || {}).map(([id, p]) => [id, p.name])) },
    titles: Object.fromEntries(Object.entries(data?.people || {}).map(([id, p]) => [id, p.title])),
    objectives: Object.fromEntries((objs?.objectives || []).map((o) => [o.id, o.title])),
    projects: Object.fromEntries((projects?.projects || []).map((p) => [p.id, p.name])),
    assignments: data?.assignments || {},
    company: "The company",
  };
  const verify = async () => {
    const { data: d } = await refetch();
    const r = d?.verification;
    if (!r) return;
    if (r.valid) toast.success(`Hash chain verified. ${int(r.count)} entries, no gaps.`);
    else toast.error(`The chain still breaks at entry ${int(r.broken_at)}.`);
  };
  const rows = data.entries.filter((e) => cat === "all" || category(e.action) === cat);
  return (
    <Page className="gap-5" data-testid="owner-audit">
      {header(v.valid ? "Nothing has been changed or removed." : "One entry doesn't match the chain.")}
      <Card className="px-5 py-4 flex flex-wrap items-center justify-between gap-4" data-testid="audit-verification">
        <div className="flex items-start gap-3 min-w-0">
          {v.valid ? <ShieldCheck className="w-5 h-5 shrink-0 text-gr-success" /> : <ShieldX className="w-5 h-5 shrink-0 text-gr-danger" />}
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className={v.valid ? "font-display text-[15px] tracking-[0.02em] font-semibold" : "font-display text-[15px] tracking-[0.02em] font-semibold text-gr-danger"}>
              {v.valid ? "Hash chain verified" : `Hash chain broken at entry ${int(v.broken_at)}`}
            </span>
            <span className="text-sm text-muted-foreground [text-wrap:pretty]">
              {v.valid ? `${int(v.count)} entries, no gaps. Checked ${ago(dataUpdatedAt)}.`
                : `Entry ${int(v.broken_at)} was changed after it was written, so the entries after it can't be trusted. Contact ${APP_NAME} support.`}
            </span>
          </div>
        </div>
        <Button variant="outline" onClick={verify} disabled={isFetching}><RefreshCw className={isFetching ? "animate-spin" : ""} />Verify again</Button>
      </Card>
      <Segmented label="Category" className="self-start" options={CATS} value={cat} onChange={setCat} testid="audit-category" />
      <div className="bg-card rounded-[10px] overflow-x-auto text-sm tabular-nums">
        <div className="min-w-[820px] grid grid-cols-[56px_minmax(0,1.6fr)_150px_minmax(0,1fr)_120px_52px] gap-3.5 px-4 py-2.5 border-b text-xs uppercase tracking-[0.09em] text-muted-foreground">
          <span>Entry</span><span>What happened</span><span>Who</span><span>On</span><span>Hash</span><span className="text-right">Time</span>
        </div>
        {rows.length === 0 && <div className="min-w-[820px] px-4 py-3 text-muted-foreground">Nothing in this category yet.</div>}
        {rows.map((e) => {
          const [name, role] = who(e, n);
          const bad = !v.valid && e.seq === v.broken_at;
          return (
            <div key={e.seq} className="min-w-[820px] grid grid-cols-[56px_minmax(0,1.6fr)_150px_minmax(0,1fr)_120px_52px] gap-3.5 items-start px-4 py-2.5 border-b last:border-b-0"
              data-testid={`audit-row-${e.seq}`}>
              <span className="text-muted-foreground">{int(e.seq)}</span>
              <div className="flex flex-col min-w-0">
                {bad && <span className="text-gr-danger font-semibold">Doesn't match the chain</span>}
                <span className="text-base text-foreground">{label(e)}</span>
                {/* the action id and the hash stay monospace: people check them against the chain */}
                <span className="font-mono text-xs text-muted-foreground">{e.action.replace(/owner/g, "founder")}</span>
              </div>
              <div className="flex flex-col"><span>{name}</span><span className="text-muted-foreground">{role}</span></div>
              <span className="text-muted-foreground [overflow-wrap:anywhere]">{on(e, n)}</span>
              <span className="font-mono text-muted-foreground">{(e.hash || "").slice(0, 12)}…</span>
              <span className="text-right text-muted-foreground">{new Date(e.ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          );
        })}
      </div>
      <p className="m-0 text-sm text-muted-foreground [text-wrap:pretty]">
        Each entry stores the hash of the one before it, so changing or removing any entry breaks every hash after it. Nobody can edit this log, including you and {APP_NAME} staff.
      </p>
    </Page>
  );
}
